// Replay (owner, 2026-10-10): « A time slider for today (and a date picker).
// Drag it → the map shows where every client was at that moment. Play button:
// watch the day in 1 minute. For checking what happened at night. »
import { useEffect, useRef } from 'react';
import { useT } from '@/lib/i18n';

const DAY = 24 * 3_600_000;
const PLAY_MS = 60_000;

/** Local midnight (Casablanca) of the day `offset` days before today. */
export function casablancaMidnight(nowMs: number, offset = 0): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(nowMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const since = ((get('hour') * 60 + get('minute')) * 60 + get('second')) * 1000;
  return Math.floor((nowMs - since) / 1000) * 1000 - offset * DAY;
}

export function ReplayBar({ now, dayOffset, setDayOffset, at, setAt, playing, setPlaying, compact }: {
  now: number;
  dayOffset: number;
  setDayOffset: (d: number) => void;
  /** Null = live. */
  at: number | null;
  setAt: (t: number | null) => void;
  playing: boolean;
  setPlaying: (p: boolean) => void;
  compact?: boolean;
}) {
  const { t, lang } = useT();
  const start = casablancaMidnight(now, dayOffset);
  const end = dayOffset === 0 ? now : start + DAY;
  const value = at ?? end;

  // ▶ — the whole day in one minute, then back to live.
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) return;
    const from = at != null && at < end - 60_000 ? at : start;
    const t0 = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - t0) / (PLAY_MS * ((end - from) / (end - start || 1))));
      const v = from + (end - from) * p;
      setAt(v);
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else { setPlaying(false); if (dayOffset === 0) setAt(null); }
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const clock = (ms: number) => new Date(ms).toLocaleTimeString(lang === 'en' ? 'en-GB' : 'fr-FR', {
    timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit',
  });
  const dayLabel = (d: number) => d === 0 ? t('Aujourd’hui') : d === 1 ? t('Hier')
    : new Date(casablancaMidnight(now, d) + 12 * 3_600_000).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { timeZone: 'Africa/Casablanca', weekday: 'short', day: '2-digit', month: '2-digit' });
  const live = at == null && dayOffset === 0;

  return (
    <div className={`flex items-center gap-2.5 ${compact ? 'flex-wrap' : ''}`}>
      <button type="button" onClick={() => setPlaying(!playing)} aria-label={playing ? t('Pause') : t('Revoir la journée en 1 minute')}
        className="h-9 w-9 shrink-0 rounded-full border border-border-subtle bg-bg-card text-text-secondary grid place-items-center cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
        <span aria-hidden className="text-[12px] leading-none">{playing ? '❚❚' : '▶'}</span>
      </button>
      <select value={dayOffset} onChange={(e) => { setPlaying(false); setDayOffset(Number(e.target.value)); setAt(Number(e.target.value) === 0 ? null : casablancaMidnight(now, Number(e.target.value)) + DAY - 60_000); }}
        aria-label={t('Jour')}
        className="h-9 rounded-lg border border-border-subtle bg-bg-card px-2 text-[12.5px] text-text-primary cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
        {Array.from({ length: 14 }, (_, d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
      </select>
      <input type="range" min={start} max={end} step={60_000} value={value} aria-label={t('Heure')}
        onChange={(e) => { setPlaying(false); const v = Number(e.target.value); setAt(dayOffset === 0 && v >= end - 60_000 ? null : v); }}
        className="flex-1 min-w-[140px] accent-brand-500 cursor-pointer" />
      <span className={`shrink-0 text-[12px] tabular-nums ${live ? 'text-text-tertiary' : 'font-semibold text-brand-700 dark:text-brand-300'}`}>
        {live ? t('en direct') : clock(value)}
      </span>
      {!live && (
        <button type="button" onClick={() => { setPlaying(false); setDayOffset(0); setAt(null); }}
          className="shrink-0 h-9 rounded-lg border border-border-subtle px-3 text-[12px] text-text-primary cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          {t('Revenir au direct')}
        </button>
      )}
    </div>
  );
}
